import { canonicalIdBase } from './schema-id.util';
import type { SchemaOrgNode } from './schema-org-node.model';

/**
 * Shared Allstate Roadside Services structured-data helpers. The upsell is
 * provided by *Allstate*, not Aceable, and is rendered by two components
 * (`rsa` on category pages, `rsa-product` on product pages). This module is the
 * single source of truth for the Allstate `Organization` provider and the
 * `Service` node both components emit, so the provider can never drift between
 * them (EPS-3923 AC2). It lives alongside the other schema builders
 * (organization.schema.ts, product-course-schema.ts, blog-posting.schema.ts).
 */

/**
 * Canonical Allstate Organization `@id`. MUST stay distinct from Aceable's
 * `https://www.{brand}/#organization` node and carry no cross-reference to it
 * (EPS-3923 AC4). It is a stable, brand-independent URI — never derive it from
 * the page or request URL (per the org-id guidance in organization.schema.ts).
 */
export const ALLSTATE_ORG_ID = 'https://www.allstate.com/#organization';
const ALLSTATE_NAME = 'Allstate';
const ALLSTATE_SAME_AS = 'https://www.allstate.com';
const SERVICE_ID_SUFFIX = '#allstate-roadside-service';

/**
 * Default Allstate Roadside Services description copy. Lives here (not only in
 * component code) so the `Service` schema and the rendered UI fall back to the
 * SAME text when Craft supplies nothing (EPS-3923 AC3). The `rsa`/`rsa-product`
 * components re-export this as their `defaultDescription`.
 */
export const ALLSTATE_DEFAULT_DESCRIPTION = `<p>20% of crashes happen while stuck on the side of the road according to the Federal Highway Administration. Make sure you're covered with Allstate Roadside Services®. Only $5/month after your free trial. Cancel anytime.</p>`;

/**
 * Standalone Allstate `Organization` node — the `Service.provider`. Emitted as
 * its own `@graph` node (deduped by `@id` in ServerSchemaService) so the
 * provider reference always resolves and carries `sameAs` for entity matching.
 */
export function getAllstateOrganizationSchema(): SchemaOrgNode {
  return {
    '@type': 'Organization',
    '@id': ALLSTATE_ORG_ID,
    name: ALLSTATE_NAME,
    sameAs: ALLSTATE_SAME_AS,
  };
}

/** Inline provider reference pointing at the standalone Allstate Organization node. */
function allstateProviderRef(): SchemaOrgNode {
  return { '@type': 'Organization', '@id': ALLSTATE_ORG_ID, name: ALLSTATE_NAME };
}

/**
 * Builds the Allstate Roadside Services `Service` node plus its standalone
 * Allstate `Organization` provider node. Shared by `rsa` and `rsa-product` so
 * both emit the SAME provider (EPS-3923 AC2). `relatedCourseId` (rsa-product
 * only) adds an `isRelatedTo` reference to the page's xGrit Course node. Returns
 * the node pair so the caller registers both in a single `register()` call.
 * Pure function — no DOM, no DI, SSR-safe.
 */
export function buildAllstateServiceSchema(
  pageUrl: string,
  name: string,
  description: string,
  relatedCourseId?: string | null,
): SchemaOrgNode[] {
  const service: SchemaOrgNode = {
    '@type': 'Service',
    '@id': `${canonicalIdBase(pageUrl)}${SERVICE_ID_SUFFIX}`,
    name,
    areaServed: 'US',
    provider: allstateProviderRef(),
  };
  if (description) service['description'] = description;
  if (relatedCourseId) service['isRelatedTo'] = { '@id': relatedCourseId };
  return [service, getAllstateOrganizationSchema()];
}
